import fetchJSON from '../fetchJSON';
import {getLocalStorageItem} from '../localStorage';
import differenceBy from 'lodash/differenceBy';
import intersectionBy from 'lodash/intersectionBy';
import fetchUser from './fetchUser';
import {isArray, isString, isBoolean, get, isNumber, uniqBy, fromPairs} from 'lodash';
import every from 'lodash/every';
import fetchDefaultUserLayers from './fetchDefaultUserLayers';

export default async (urlLayers: string[] | undefined): Promise<{
    savedUserLayers: UserLayer[];
    userLayers: UserLayer[];
    isUserLayersEphemeral: boolean;
}> => {
    const savedUserLayers = await fetchSavedUserLayers();

    if (urlLayers && urlLayers.length) {
        const opacities = fromPairs(savedUserLayers.map(userLayer =>
            [userLayer.layer.type, userLayer.layer.opacity]
        ));
        const userLayers = urlLayers.map((type: string) => ({
            isVisible: true,
            layer: {type, opacity: get(opacities, type, 1)}
        }));

        return {
            isUserLayersEphemeral: true,
            savedUserLayers,
            userLayers: uniqBy([
                ...userLayers,
                ...savedUserLayers.map(userLayer => ({...userLayer, isVisible: false}))
            ], 'layer.type')
        };
    } else {
        return {
            isUserLayersEphemeral: false,
            savedUserLayers,
            userLayers: savedUserLayers
        };
    }
};

async function fetchSavedUserLayers() {
    if ((await fetchUser()).isAuthenticated) {
        const localUserLayers = getLocalStorageItem<UserLayer[]>(
            'userLayers',
            [],
            isValidUserLayers
        );

        const remoteUserLayers = (await fetchJSON.memoized('/api/objects/mapSource/tilejson/', {
            credentials: 'same-origin'
        })).map((tilejson: any) => ({
            isVisible: false,
            layer: {
                opacity: 1,
                type: tilejson.uniqueTilecacheKey
            }
        })) as UserLayer[];

        const userLayers = [
            ...intersectionBy(localUserLayers, remoteUserLayers, 'layer.type'),
            ...differenceBy(remoteUserLayers, localUserLayers, 'layer.type'),
        ];

        if (userLayers.length) return userLayers;
    }

    return await fetchDefaultUserLayers();
}

function isValidUserLayers(userLayers: UserLayer[]) {
    if (!isArray(userLayers)) return false;
    return every(userLayers, userLayer => (
        isBoolean(userLayer.isVisible) &&
        isString(userLayer.layer.type) &&
        isNumber(userLayer.layer.opacity)
    ));
}
